import "./open-text.less"
import PresentationQuestion from "../components/media/presentation-question"
import { classNames, hasText } from "utils/helpers"
import { ChangeEvent, useLayoutEffect, useRef, useState } from "react"
import PresentationImage from "../components/media/presentation-image"
import { PlayOpenTextState } from "play/play-model"
import { usePlayState } from "play/hooks/use-play-state"
import { requestNextScreenReducer } from "play/reducers"
import { ScreenType } from "models/tables"
import { useMessages } from "play/language/language"

export default function OpenText() {
  const {
    finished,
    screen,
    openText,
    isTeacherUrl,
    isPreview,
    modifyState,
  } = usePlayState()

  function submitAnswer() {
    modifyState((state) => {
      requestNextScreenReducer(state)
    })
  }

  function answerChanged(newText: string) {
    modifyState((state) => {
      if (!state.screen) {
        console.error("No screen!")
        return
      }
      if (state.screen.screenType !== ScreenType.OpenText) {
        console.error("Not open text!")
        return
      }
      if (state.finished) {
        // console.log("Finished!");
        return
      }
      if (state.isTeacherUrl) {
        return
      }

      state.openText.answer = newText
      state.nextScreenButtonIsActive = hasText(state.openText.answer)
    })
  }

  return (
    <OpenTextScreenView
      questionText={screen?.questionText}
      questionImage={screen?.questionImage}
      questionImageWidth={screen?.questionImageWidth || 0}
      questionImageHeight={screen?.questionImageHeight || 0}
      answer={openText.answer}
      isTeacher={isTeacherUrl}
      finished={finished}
      isPreview={isPreview}
      onSubmitAnswer={submitAnswer}
      onAnswerChanged={answerChanged}
    />
  )
}

type Props = {
  finished: boolean
  questionText: string | undefined
  questionImage: string | undefined
  questionImageWidth: number
  questionImageHeight: number
  answer: PlayOpenTextState["answer"]
  isTeacher: boolean
  isPreview: boolean
  onSubmitAnswer(): void
  onAnswerChanged(newText: string): void
}

type State = {}

export function OpenTextScreenView(props: Props) {
  const {
    questionText,
    questionImage,
    questionImageWidth,
    questionImageHeight,
    answer,
    finished,
    isTeacher,
    isPreview,
    onSubmitAnswer,
    onAnswerChanged,
  } = props

  const [forceRedraw, setForceRedraw] = useState(0)
  const imgContainerRef = useRef<HTMLDivElement>(null)
  const zoomContainerRef = useRef<HTMLDivElement>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const msg = useMessages()

  // force update
  useLayoutEffect(() => {
    setForceRedraw((prev) => prev + 1)
  }, [imgContainerRef.current])

  useLayoutEffect(() => {
    setForceRedraw((prev) => prev + 1)
  }, [isZoomed])

  return (
    <div
      className={classNames("open-text-screen play-screen", {
        zoomed: isZoomed,
        w:
          questionImageWidth &&
          questionImageHeight &&
          questionImageWidth > questionImageHeight,
        h:
          questionImageWidth &&
          questionImageHeight &&
          questionImageWidth < questionImageHeight,
        "has-image": hasText(questionImage),
      })}
    >
      <div
        className={classNames("open-text-top", {
          "has-image": hasText(questionImage),
        })}
        ref={zoomContainerRef}
      >
        {hasText(questionImage) && (
          <div
            ref={imgContainerRef}
            style={{ overflow: "hidden", background: "transparent" }}
          >
            <PresentationImage
              className="open-text-image"
              url={questionImage || ""}
              imageWidth={questionImageWidth || 0}
              imageHeight={questionImageHeight || 0}
              isZoomed={isZoomed}
              modifyHeight={false}
              toggleZoom={() => {
                setIsZoomed((prev) => !prev)
              }}
              alt={questionText || ""}
              zoomContainer={isZoomed ? zoomContainerRef : imgContainerRef}
            />
          </div>
        )}
        <PresentationQuestion
          className="question-top"
          question={questionText}
        />
      </div>
      <div className="open-text-bottom">
        <PresentationQuestion
          className="question-bottom"
          question={questionText}
        />

        {isZoomed === false && (
          <div className="input-area">
            <div className="input-wrapper">
              <textarea
                className="input"
                autoFocus={!isPreview}
                inputMode={finished ? "none" : undefined}
                readOnly={finished || isTeacher}
                onChange={(event) => onAnswerChanged(event.currentTarget.value)}
                value={answer}
                role={msg.answers}
                style={{ height: "30vh" }}
                disabled={
                  isPreview || finished || isTeacher
                }
              />
              <button
                className="next"
                onClick={onSubmitAnswer}
                disabled={
                  finished || !hasText(answer) || isPreview || isTeacher
                }
              >
                <i />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
